export interface AudioProcessorConfig {
  sampleRate: number;
  channels: number;
  bitDepth: number;
  bufferSize: number;
}

export class AudioProcessor {
  private config: AudioProcessorConfig;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private isProcessing: boolean = false;

  constructor(config: AudioProcessorConfig) {
    this.config = {
      sampleRate: 16000,
      channels: 1,
      bitDepth: 16,
      bufferSize: 4096,
      ...config
    };
  }

  /**
   * Check if audio processing is supported
   */
  public static isSupported(): boolean {
    return typeof window !== 'undefined' && 
           ('webkitAudioContext' in window || 'AudioContext' in window);
  }

  /**
   * Initialize audio context and processor
   */
  public async initialize(): Promise<boolean> {
    try {
      if (!AudioProcessor.isSupported()) {
        console.error("❌ AudioContext not supported in this browser");
        throw new Error("AudioContext not supported in this browser");
      }

      const AudioContextClass = (window as any).webkitAudioContext || (window as any).AudioContext;
      
      this.audioContext = new AudioContextClass({
        sampleRate: this.config.sampleRate,
        latencyHint: 'interactive'
      });

      this.processor = this.audioContext.createScriptProcessor(
        this.config.bufferSize,
        this.config.channels,
        1
      );

      return true;

    } catch (error) {
      console.error("❌ Audio processor initialization failed:", error);
      return false;
    }
  }

  /**
   * Process audio stream and convert to Google Cloud format
   */
  public processAudioStream(
    stream: MediaStream,
    onAudioData: (audioData: Uint8Array) => void
  ): boolean {
    try {
      if (!this.audioContext || !this.processor) {
        console.error("❌ Audio processor not initialized");
        throw new Error("Audio processor not initialized");
      }

      if (this.isProcessing) {
        return false;
      }

      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      this.processor.onaudioprocess = (event: any) => {
        if (!this.isProcessing) {
          return;
        }

        try {
          const inputBuffer = event.inputBuffer;
          const audioData = this.convertToGoogleCloudFormat(inputBuffer);
          
          if (audioData && audioData.length > 0) {
            onAudioData(audioData);
          }
        } catch (error) {
          console.error("❌ Error processing audio data:", error);
        }
      };

      this.isProcessing = true;
      return true;

    } catch (error) {
      console.error("Failed to process audio stream:", error);
      return false;
    }
  }

  /**
   * Convert audio buffer to Google Cloud Speech format
   */
  private convertToGoogleCloudFormat(inputBuffer: AudioBuffer): Uint8Array {
    try {
      const channelData = inputBuffer.getChannelData(0); // Get first channel
      const samples = channelData.length;
      
      // Convert float32 to 16-bit PCM
      const pcm16 = new Int16Array(samples);
      
      for (let i = 0; i < samples; i++) {
        // Convert float32 (-1.0 to 1.0) to int16 (-32768 to 32767)
        const sample = Math.max(-1, Math.min(1, channelData[i]));
        pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      }

      // Convert to Uint8Array for transmission
      const result = new Uint8Array(pcm16.buffer);
      
      return result;
      
    } catch (error) {
      console.error("❌ Failed to convert audio format:", error);
      return new Uint8Array(0);
    }
  }

  /**
   * Process multiple audio streams and mix them
   */
  public processDualStreams(
    localStream: MediaStream,
    remoteStream: MediaStream,
    onAudioData: (audioData: Uint8Array, source: 'local' | 'remote' | 'mixed') => void
  ): boolean {
    try {
      if (!this.audioContext || !this.processor) {
        throw new Error("Audio processor not initialized");
      }

      if (this.isProcessing) {
        return false;
      }

      // Create sources for both streams
      const localSource = this.audioContext.createMediaStreamSource(localStream);
      const remoteSource = this.audioContext.createMediaStreamSource(remoteStream);

      // Create a mixer to combine streams
      const mixer = this.audioContext.createGain();
      const localGain = this.audioContext.createGain();
      const remoteGain = this.audioContext.createGain();

      // Set gain levels (adjust as needed)
      localGain.gain.value = 0.8;  // Local audio slightly lower
      remoteGain.gain.value = 1.0; // Remote audio at full level

      // Connect streams to mixer
      localSource.connect(localGain);
      remoteSource.connect(remoteGain);
      localGain.connect(mixer);
      remoteGain.connect(mixer);

      // Connect mixer to processor
      mixer.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      this.processor.onaudioprocess = (event: any) => {
        if (!this.isProcessing) return;

        try {
          const inputBuffer = event.inputBuffer;
          
          // Process mixed audio
          const mixedAudioData = this.convertToGoogleCloudFormat(inputBuffer);
          if (mixedAudioData && mixedAudioData.length > 0) {
            onAudioData(mixedAudioData, 'mixed');
          }

        } catch (error) {
          // Silent error handling for production
        }
      };

      this.isProcessing = true;
      return true;

    } catch (error) {
      return false;
    }
  }

  /**
   * Stop audio processing
   */
  public stopProcessing(): void {
    this.isProcessing = false;
    
    if (this.processor) {
      this.processor.disconnect();
    }
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    this.stopProcessing();
    
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Get current processing status
   */
  public getStatus(): {
    isInitialized: boolean;
    isProcessing: boolean;
    sampleRate: number;
    channels: number;
    bitDepth: number;
  } {
    return {
      isInitialized: this.audioContext !== null,
      isProcessing: this.isProcessing,
      sampleRate: this.config.sampleRate,
      channels: this.config.channels,
      bitDepth: this.config.bitDepth
    };
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<AudioProcessorConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}
